"use client"

import { useState, useEffect, useRef } from "react"
import { Outlet, useNavigate, useLocation } from "react-router-dom"
import Sidebar from "../../components/Sidebar"
import { useAuth } from "../../context/AuthProvider"
import { getRecentInquiries, logoutUser, getTicketsByAgent, getInquiryByClientId, getTicketCountsByAgentId } from "../../services/AgentServices"

const readStoredValue = (key) => sessionStorage.getItem(key) || localStorage.getItem(key) || ""

const normalizeStatus = (status) => String(status || "").toUpperCase().trim()
const normalizeStatusCode = (status) => {
  const value = normalizeStatus(status)
  if (["OPEN", "OPN"].includes(value)) return "OPN"
  if (["IN-PROGRESS", "INPROGRESS", "IN_PROGRESS", "INP"].includes(value)) return "INP"
  if (["RESOLVED", "CLOSED", "CRS", "DONE"].includes(value)) return "CRS"
  if (["NO-RESPONSE", "NO RESPONSE", "NORESPONSE", "CNR"].includes(value)) return "CNR"
  return value
}

const isAssignedInquiryForUser = (inquiry, userId) => {
  const normalizedUserId = Number.parseInt(userId)
  const assignedId = Number.parseInt(inquiry?.assigned_to ?? inquiry?.assignedTo, 10)
  return assignedId === normalizedUserId
}

const getDateOnly = (value) => {
  if (!value) return ""
  const normalized = String(value).trim()

  if (!normalized || normalized === "0000-00-00" || normalized === "0000-00-00 00:00:00") {
    return ""
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(normalized)) {
    return normalized.slice(0, 10)
  }

  const parsed = new Date(normalized)
  if (Number.isNaN(parsed.getTime())) return ""

  return [
    parsed.getFullYear(),
    String(parsed.getMonth() + 1).padStart(2, "0"),
    String(parsed.getDate()).padStart(2, "0"),
  ].join("-")
}

const AgentLayout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, user } = useAuth()
  const [searchQuery, setSearchQuery] = useState("")
  const [showNotifications, setShowNotifications] = useState(false)
  const [notificationCount, setNotificationCount] = useState(0)
  const [recentInquiries, setRecentInquiries] = useState([])
  const [agentMenuVisibility, setAgentMenuVisibility] = useState({
    showTickets: true,
    showInquiries: true,
  })
  const [followupReminder, setFollowupReminder] = useState(null)
  const notificationRef = useRef(null)

  const getPageTitle = () => {
    switch (location.pathname) {
      case "/agent/":
        return "Staff Dashboard"
      case "/agent/components/Tickets":
        return "Ticket Summary"
      case "/agent/components/Callbackrequest":
        return "Callback Requests"
      case "/agent/components/Selfassesment":
        return "Self Assessment Report"
      case "/agent/components/ManageProfile":
        return "My Profile"
      case "/agent/components/Help":
        return "Help and Support"
      case "/agent/components/Notifications":
        return "Notifications"
      default:
        return "Staff Dashboard"
    }
  }

  const handleLogout = async () => {
    const userId = readStoredValue("userId")
    try {
      if (userId) {
        await logoutUser(userId)
      }
    } catch (error) {
      console.error("Error during logout:", error)
    } finally {
      logout()
      navigate("/")
    }
  }

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setShowNotifications(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    const fetchNotificationCount = async () => {
      const clientId = readStoredValue("clientId")
      if (clientId) {
        try {
          const { success, newInquiriesCount, recentInquiries: recent } = await getRecentInquiries(clientId)
          if (success) {
            setNotificationCount(newInquiriesCount)
            setRecentInquiries(recent)
          } else {
            setNotificationCount(0)
            setRecentInquiries([])
          }
        } catch (error) {
          console.error("Failed to fetch notification count:", error)
          setNotificationCount(0)
          setRecentInquiries([])
        }
      }
    }

    fetchNotificationCount()
  }, [])

  useEffect(() => {
    const fetchAgentMenuVisibility = async () => {
      const clientId = readStoredValue("clientId")
      const userId = readStoredValue("userId")

      if (!clientId || !userId) {
        setAgentMenuVisibility({ showTickets: false, showInquiries: false })
        return
      }

      try {
        const [ticketCountRes, ticketRes, inquiryRes] = await Promise.all([
          getTicketCountsByAgentId(userId).catch(() => ({ status: false, data: {} })),
          getTicketsByAgent(clientId, userId).catch(() => ({ success: false, tickets: [] })),
          getInquiryByClientId(clientId, 1, 1000).catch(() => ({ status: false, data: [] })),
        ])

        const ticketCountData = ticketCountRes?.data || {}
        const assignedTickets = Array.isArray(ticketRes?.tickets) ? ticketRes.tickets : []
        const allInquiries =
          inquiryRes && (inquiryRes.status || inquiryRes.success) && Array.isArray(inquiryRes.data)
            ? inquiryRes.data
            : []
        const assignedInquiries = allInquiries.filter((inq) => isAssignedInquiryForUser(inq, userId))

        setAgentMenuVisibility({
          showTickets:
            (ticketCountData.totalCount ||
              ticketCountData.openCount ||
              ticketCountData.inProgressCount ||
              ticketCountData.closedCount ||
              ticketCountData.noResponseCount ||
              0) > 0 ||
            assignedTickets.length > 0 ||
            location.pathname.startsWith("/agent/components/Tickets"),
          showInquiries:
            assignedInquiries.length > 0 ||
            location.pathname.startsWith("/agent/components/Inquiries") ||
            location.pathname.startsWith("/agent/components/LeadStatus"),
        })
      } catch (error) {
        console.error("Failed to derive agent menu visibility:", error)
        setAgentMenuVisibility({
          showTickets: location.pathname.startsWith("/agent/components/Tickets"),
          showInquiries:
            location.pathname.startsWith("/agent/components/Inquiries") ||
            location.pathname.startsWith("/agent/components/LeadStatus"),
        })
      }
    }

    fetchAgentMenuVisibility()
  }, [location.pathname, user?.userId, user?.clientId])

  useEffect(() => {
    const showTodayInquiryFollowupReminder = async () => {
      const clientId = readStoredValue("clientId")
      const userId = readStoredValue("userId")

      if (!clientId || !userId) return

      const today = getDateOnly(new Date())
      const reminderKey = `agent-inquiry-followups-shown:${userId}:${today}`

      if (sessionStorage.getItem(reminderKey) === "true") return

      try {
        const inquiryRes = await getInquiryByClientId(clientId, 1, 1000)
        const allInquiries =
          inquiryRes && (inquiryRes.status || inquiryRes.success) && Array.isArray(inquiryRes.data)
            ? inquiryRes.data
            : []

        const dueTodayInquiries = allInquiries.filter((inq) => {
          if (!isAssignedInquiryForUser(inq, userId)) return false

          const statusCode = normalizeStatusCode(inq?.status || inq?.status_code)
          if (!["OPN", "INP"].includes(statusCode)) return false

          const nextFollowup = inq?.Next_followup ?? inq?.next_followup ?? inq?.nextFollowup ?? ""
          return getDateOnly(nextFollowup) === today
        })

        if (dueTodayInquiries.length === 0) return

        const reminderPreview = dueTodayInquiries
          .slice(0, 3)
          .map((inq) => {
            const inquiryId = inq?.id ?? inq?.inquiry_id ?? inq?.ticket_id ?? "N/A"
            const clientName = inq?.Client_name ?? inq?.client_name ?? inq?.name ?? "Client"
            return `#${inquiryId} ${clientName}`
          })

        sessionStorage.setItem(reminderKey, "true")

        setFollowupReminder({
          title:
            dueTodayInquiries.length === 1
              ? "1 inquiry needs follow-up today."
              : `${dueTodayInquiries.length} inquiries need follow-up today.`,
          subtitle: `Showing Open and In-Progress inquiries scheduled for ${today}.`,
          preview: `${reminderPreview.join(", ")}${dueTodayInquiries.length > reminderPreview.length ? " ..." : ""}`,
        })
      } catch (error) {
        console.error("Failed to load inquiry follow-up reminder:", error)
      }
    }

    showTodayInquiryFollowupReminder()
  }, [user?.userId, user?.clientId])

  return (
    <div className="d-flex flex-column min-vh-100">
      {followupReminder && (
        <div className="followup-popup-shell">
          <div className="followup-popup-card">
            <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
              <div>
                <p className="mb-1 fw-semibold">{followupReminder.title}</p>
                <div className="small text-muted">{followupReminder.subtitle}</div>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-light border followup-popup-close"
                onClick={() => setFollowupReminder(null)}
                aria-label="Close notification"
              >
                x
              </button>
            </div>
            <div className="small mb-3">{followupReminder.preview}</div>
            <div className="d-flex justify-content-end gap-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => {
                  setFollowupReminder(null)
                  navigate("/agent/components/Inquiries", {
                    state: { followupToday: true },
                  })
                }}
                type="button"
              >
                View inquiries
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setFollowupReminder(null)}
                type="button"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="navbar navbar-expand-lg text-white px-3 py-2 shadow-sm" style={{ backgroundColor: "#1e293b" }}>
        <div className="container-fluid">
          <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center">
            <span className="navbar-brand fw-bold mb-1 mb-lg-0 me-lg-4 text-white">{getPageTitle()}</span>
            <div className="d-flex flex-wrap align-items-center gap-3 small opacity-90">
              <div className="d-flex align-items-center">
                <i className="fas fa-user me-1"></i>
                <span className="fw-medium">{user?.name || user?.username || "Agent User"}</span>
              </div>
            </div>
          </div>

          <button
            className="navbar-toggler border-0"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#agentNavbar"
            aria-controls="agentNavbar"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className="collapse navbar-collapse" id="agentNavbar">
            <div className="d-flex ms-auto my-2 my-lg-0">
              <button
                className="btn btn-outline-light border-0"
                onClick={handleLogout}
                title="Logout"
                style={{ backgroundColor: "#374151" }}
              >
                <i className="fas fa-sign-out-alt me-1"></i>
                <span className="d-none d-sm-inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex flex-grow-1 agent-layout-shell">
        <Sidebar role="agent" showLabel={false} agentMenuVisibility={agentMenuVisibility} />

        <main className="flex-grow-1 p-2 p-md-3 bg-light overflow-auto agent-layout-main">
          <Outlet context={{ searchQuery, setSearchQuery }} />
        </main>
      </div>

      <footer className="text-white text-center py-2 mt-auto agent-layout-footer" style={{ backgroundColor: "#1e293b" }}>
        <small>© {new Date().getFullYear()} Powered By ATai</small>
      </footer>

      <style>{`
        .followup-popup-shell {
          position: fixed;
          top: 76px;
          right: 16px;
          z-index: 1080;
          width: min(360px, calc(100vw - 24px));
        }

        .followup-popup-card {
          border-radius: 20px;
          background: linear-gradient(145deg, #ffffff 0%, #f8fbff 100%);
          border: 1px solid #dbeafe;
          box-shadow: 0 20px 45px rgba(15, 23, 42, 0.16);
          padding: 16px;
        }

        .followup-popup-close {
          width: 32px;
          height: 32px;
          border-radius: 999px;
          padding: 0;
          line-height: 1;
          flex-shrink: 0;
        }

        @media (max-width: 767.98px) {
          .followup-popup-shell {
            top: 72px;
            left: 12px;
            right: 12px;
            width: auto;
          }

          .agent-layout-shell {
            display: block !important;
          }

          .agent-layout-main {
            padding-bottom: calc(92px + env(safe-area-inset-bottom)) !important;
          }

          .agent-layout-footer {
            display: none;
          }

          .navbar .container-fluid {
            align-items: flex-start;
          }
        }
      `}</style>
    </div>
  )
}

export default AgentLayout
